import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../services/supabaseClient';
import Navbar from '../components/Navbar';

export default function AuthDebugPage() {
  const { user, isLoading } = useAuth();
  const [sessionData, setSessionData] = useState<any>(null);
  const [localStorageData, setLocalStorageData] = useState<Record<string, string>>({});
  const [sessionStorageData, setSessionStorageData] = useState<Record<string, string>>({});
  const [supabaseUrl, setSupabaseUrl] = useState<string>('');
  const [refreshResult, setRefreshResult] = useState<any>(null);
  const [refreshError, setRefreshError] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // Get Supabase URL
    setSupabaseUrl(import.meta.env.VITE_SUPABASE_URL || 'Not defined');

    // Get current session
    const checkSession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        setSessionData({ data, error });
      } catch (err) {
        console.error('Error checking session:', err);
      }
    };
    
    checkSession();

    // Get localStorage and sessionStorage data
    const lsData: Record<string, string> = {};
    const ssData: Record<string, string> = {};
    
    // Get auth-related localStorage items
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') || key.startsWith('supabase') || key.startsWith('auth_') || key.startsWith('sf_')) {
        try {
          lsData[key] = localStorage.getItem(key) || '';
        } catch (e) {
          lsData[key] = 'Error reading value';
        }
      }
    });
    
    // Get auth-related sessionStorage items
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('sb-') || key.startsWith('supabase') || key.startsWith('auth_') || key.startsWith('sf_')) {
        try {
          ssData[key] = sessionStorage.getItem(key) || '';
        } catch (e) {
          ssData[key] = 'Error reading value';
        }
      }
    });
    
    setLocalStorageData(lsData);
    setSessionStorageData(ssData);
  }, []);

  const handleRefreshSession = async () => {
    setIsRefreshing(true);
    try {
      const { data, error } = await supabase.auth.refreshSession();
      setRefreshResult(data);
      
      if (error) {
        setRefreshError(error.message);
      } else {
        // Refresh the session data after refreshing
        const { data: newData } = await supabase.auth.getSession();
        setSessionData({ data: newData });
        setRefreshError('');
      }
    } catch (err) {
      setRefreshError(`Error: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleClearAuth = () => {
    // Clear all auth-related storage
    const authKeys = Object.keys(localStorage).filter(k => 
      k.startsWith('sb-') || k.startsWith('supabase') || k.startsWith('auth_') || k.startsWith('sf_')
    );
    authKeys.forEach(key => localStorage.removeItem(key));
    
    const sessionKeys = Object.keys(sessionStorage).filter(k => 
      k.startsWith('sb-') || k.startsWith('supabase') || k.startsWith('auth_') || k.startsWith('sf_')
    );
    sessionKeys.forEach(key => sessionStorage.removeItem(key));
    
    // Force sign out
    supabase.auth.signOut();
    
    // Refresh the page after a small delay
    setTimeout(() => {
      window.location.href = '/login?fresh=true';
    }, 500);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar isPublic={!user} />
      <div className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              <h1 className="text-lg font-medium text-gray-900">Authentication Debug</h1>
              
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h2 className="text-md font-medium text-gray-900">Supabase Configuration</h2>
                <p className="mt-1 text-sm text-gray-500">URL: {supabaseUrl}</p>
              </div>
              
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h2 className="text-md font-medium text-gray-900">Session Status</h2>
                <div className="mt-1 text-sm text-gray-500">
                  <p>Auth Loading: {isLoading ? 'True' : 'False'}</p>
                  <p>User: {user ? user.email : 'Not authenticated'}</p>
                  <p>Session: {sessionData?.data?.session ? 'Active' : 'No active session'}</p>
                  <p>Access Token: {sessionData?.data?.session?.access_token ? 
                    `${sessionData.data.session.access_token.substring(0, 10)}...` : 'None'}</p>
                </div>
              </div>
              
              <div className="mt-4 flex space-x-4">
                <button
                  onClick={handleRefreshSession}
                  disabled={isRefreshing}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-blue-300"
                >
                  {isRefreshing ? 'Refreshing...' : 'Refresh Session'}
                </button>
                
                <button
                  onClick={handleClearAuth}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  Clear Auth & Logout
                </button>
              </div>
              
              {refreshError && (
                <div className="mt-2 text-sm text-red-600">
                  Error refreshing session: {refreshError}
                </div>
              )}
              
              {refreshResult && (
                <div className="mt-4 border-t border-gray-200 pt-4">
                  <h2 className="text-md font-medium text-gray-900">Refresh Result</h2>
                  <div className="mt-1 overflow-auto max-h-40 text-xs font-mono bg-gray-100 p-2 rounded">
                    <pre>{JSON.stringify(refreshResult, null, 2)}</pre>
                  </div>
                </div>
              )}
              
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h2 className="text-md font-medium text-gray-900">Local Storage Data</h2>
                <div className="mt-1 overflow-auto max-h-40 text-xs font-mono bg-gray-100 p-2 rounded">
                  <pre>{JSON.stringify(localStorageData, null, 2)}</pre>
                </div>
              </div>
              
              <div className="mt-4 border-t border-gray-200 pt-4">
                <h2 className="text-md font-medium text-gray-900">Session Storage Data</h2>
                <div className="mt-1 overflow-auto max-h-40 text-xs font-mono bg-gray-100 p-2 rounded">
                  <pre>{JSON.stringify(sessionStorageData, null, 2)}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}